import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../utils/api";
import HeaderPublic from "../components/HeaderPublic";
import FooterPublic from "../components/FooterPublic";
import { validatePassword, PASSWORD_HINT } from "../utils/passwordRules";
import "../components/Login.css";
import "./ForgotPassword.css";

// How long the "resend code" button stays disabled after a send. Matches the
// reader's own cooldown (see reader/controllers/password_reset_email.go) —
// this is only the visible half of it; the server enforces the real one.
const RESEND_COOLDOWN_SEC = 60;

// The login screen's self-service reset, dressed as the same screen: same
// gradient, same black card, same logo — arriving here should not feel like
// leaving the panel. Two steps in one page: ask for the email, then — whether
// or not that email actually has an account, the server never says — ask for
// the code and a new password. Never telling the two cases apart is the
// point: this screen must not be a way to find out which emails are
// registered.
export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState("request"); // "request" | "confirm" | "done"
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const cooldownTimer = useRef(null);
  useEffect(() => () => clearInterval(cooldownTimer.current), []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SEC);
    clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const requestCode = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Enter your email address.");
      return;
    }

    setSending(true);
    try {
      // The server answers the same way whether or not this email has an
      // account — that's the point, not a bug to work around here. The
      // confirm screen's own lead line covers what happened; no separate
      // notice needed for this first send.
      await api.post("/forgot-password", { email: email.trim() });
      setStep("confirm");
      startCooldown();
    } catch {
      setError("Could not send the code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || sending) return;
    setError("");
    setSending(true);
    try {
      await api.post("/forgot-password", { email: email.trim() });
      setNotice("Sent again. Check your inbox.");
      startCooldown();
    } catch {
      setError("Could not resend the code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("Enter the code from your email.");
      return;
    }
    if (!password || !confirm) {
      setError("Please fill in both password fields.");
      return;
    }
    if (password !== confirm) {
      setError("The passwords do not match.");
      return;
    }
    if (!validatePassword(password)) {
      setError(PASSWORD_HINT);
      return;
    }

    setSending(true);
    try {
      await api.post("/forgot-password/confirm", {
        email: email.trim(),
        otp: otp.trim(),
        password,
      });
      setStep("done");
    } catch (err) {
      setError(
        err?.response?.status === 400
          ? "The server rejected that password. " + PASSWORD_HINT
          : "That code is invalid or has expired. Request a new one."
      );
    } finally {
      setSending(false);
    }
  };

  let body;
  if (step === "done") {
    body = (
      <div className="login-form">
        <p className="fp-lead">
          Your password has been reset. Sign in with your new one.
        </p>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
        >
          Back to sign in
        </button>
      </div>
    );
  } else if (step === "confirm") {
    body = (
      <form onSubmit={confirmReset} className="login-form">
        <p className="fp-lead">
          We sent a code to <strong>{email}</strong>. It's valid for 15 minutes.
        </p>

        {notice && <div className="fp-notice">{notice}</div>}
        {error && <div className="error-msg">{error}</div>}

        <label htmlFor="fp-otp">Code</label>
        <input
          id="fp-otp"
          className="fp-otp-input"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          disabled={sending}
        />

        <label htmlFor="fp-new">New password</label>
        <input
          id="fp-new"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={sending}
        />

        <label htmlFor="fp-confirm">Confirm password</label>
        <input
          id="fp-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={sending}
        />

        <p className="fp-hint">{PASSWORD_HINT}</p>

        <button type="submit" disabled={sending}>
          {sending ? "Saving…" : "Reset password"}
        </button>

        <button
          type="button"
          className="login-forgot fp-resend"
          onClick={resend}
          disabled={sending || cooldown > 0}
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
        </button>
      </form>
    );
  } else {
    body = (
      <form onSubmit={requestCode} className="login-form">
        <p className="fp-lead">We'll email you a reset code.</p>

        {error && <div className="error-msg">{error}</div>}

        <label htmlFor="fp-email">Email</label>
        <input
          id="fp-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sending}
        />

        <button type="submit" disabled={sending}>
          {sending ? "Sending…" : "Send code"}
        </button>

        <Link to="/login" className="login-forgot">
          Back to sign in
        </Link>
      </form>
    );
  }

  return (
    <>
      <HeaderPublic />
      <div className="login-page">
        <div className="login-container">
          <div className="login-box">
            <div className="login-logo">
              <img src="/mongrel-logo.png" alt="mongrel" className="logo-img" />
            </div>
            {body}
          </div>
        </div>
      </div>
      <FooterPublic />
    </>
  );
}
